import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, CheckCircle, AlertTriangle, Clock, Zap, BarChart3, TrendingUp, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIWeeklyReportWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/dashboard/ai-weekly-report`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) {
        console.error('AI weekly report error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  if (loading) {
    return (
      <Card className="border-violet-200 bg-violet-50/60" data-testid="ai-weekly-report-loading">
        <CardContent className="pt-6 flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const rateColor = data.success_rate >= 95 ? 'text-emerald-600' : data.success_rate >= 85 ? 'text-amber-600' : 'text-red-600';
  const rateBg = data.success_rate >= 95 ? 'bg-emerald-100 border-emerald-200' : data.success_rate >= 85 ? 'bg-amber-100 border-amber-200' : 'bg-red-100 border-red-200';

  return (
    <Card className="border-violet-200 bg-gradient-to-br from-violet-50/80 to-indigo-50/40" data-testid="ai-weekly-report">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" data-testid="ai-weekly-report-title">
          <Brain className="w-5 h-5 text-violet-600" />
          KI Performance Report (7 Tage)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Top Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ai-weekly-report-stats">
          <div className="p-3 rounded-xl bg-white border border-violet-200 shadow-sm" data-testid="ai-report-total">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-violet-600" />
              <p className="text-xs font-medium text-violet-700 uppercase tracking-wider">Anfragen</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{data.total_requests}</p>
            <p className="text-xs text-gray-500">{data.success_count} erfolgreich</p>
          </div>
          <div className={`p-3 rounded-xl border shadow-sm ${rateBg}`} data-testid="ai-report-rate">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-violet-600" />
              <p className="text-xs font-medium text-violet-700 uppercase tracking-wider">Erfolgsrate</p>
            </div>
            <p className={`text-2xl font-bold ${rateColor}`}>{data.success_rate}%</p>
            <p className="text-xs text-gray-500">{data.error_count} Fehler</p>
          </div>
          <div className="p-3 rounded-xl bg-white border border-violet-200 shadow-sm" data-testid="ai-report-latency">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-violet-600" />
              <p className="text-xs font-medium text-violet-700 uppercase tracking-wider">Latenz</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{data.avg_latency_ms}ms</p>
            <p className="text-xs text-gray-500">P95: {data.p95_latency_ms}ms</p>
          </div>
          <div className="p-3 rounded-xl bg-white border border-violet-200 shadow-sm" data-testid="ai-report-peak">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-violet-600" />
              <p className="text-xs font-medium text-violet-700 uppercase tracking-wider">Spitzenzeit</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{data.peak_hour !== null ? `${data.peak_hour}:00` : '-'}</p>
            <p className="text-xs text-gray-500">Uhr (UTC)</p>
          </div>
        </div>

        {/* Daily Chart */}
        {data.daily_breakdown.length > 0 && (
          <div className="p-4 rounded-xl bg-white border border-violet-200 shadow-sm" data-testid="ai-report-daily">
            <p className="text-xs font-medium text-violet-700 uppercase tracking-wider mb-3">Tagesübersicht</p>
            <div className="flex items-end gap-1 h-20">
              {data.daily_breakdown.map((day) => {
                const maxReq = Math.max(...data.daily_breakdown.map(d => d.requests), 1);
                const height = Math.max((day.requests / maxReq) * 100, 4);
                const errorHeight = day.errors > 0 ? Math.max((day.errors / maxReq) * 100, 2) : 0;
                return (
                  <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full relative" style={{ height: `${height}%`, minHeight: '2px' }}>
                      <div className="absolute bottom-0 w-full rounded-t-md bg-violet-400" style={{ height: '100%' }} />
                      {errorHeight > 0 && (
                        <div className="absolute bottom-0 w-full rounded-t-md bg-red-400" style={{ height: `${errorHeight}%` }} />
                      )}
                    </div>
                    <span className="text-[9px] text-gray-400">{day.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 mt-2 text-[10px] text-gray-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-400" />Anfragen</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" />Fehler</span>
            </div>
          </div>
        )}

        {/* Flow & Model Usage Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Flow Breakdown */}
          {data.flow_breakdown.length > 0 && (
            <div className="p-4 rounded-xl bg-white border border-violet-200 shadow-sm" data-testid="ai-report-flows">
              <p className="text-xs font-medium text-violet-700 uppercase tracking-wider mb-3">KI-Flows</p>
              <div className="space-y-2">
                {data.flow_breakdown.slice(0, 5).map((flow, i) => {
                  const maxCount = Math.max(...data.flow_breakdown.map(f => f.count), 1);
                  const width = Math.max((flow.count / maxCount) * 100, 2);
                  return (
                    <div key={flow.flow} className="flex items-center gap-2" data-testid={`ai-report-flow-${i}`}>
                      <span className="text-xs text-gray-700 w-24 flex-shrink-0 truncate" title={flow.flow}>{flow.flow}</span>
                      <div className="flex-1 h-4 bg-violet-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-violet-400 to-indigo-400" style={{ width: `${width}%` }} />
                      </div>
                      <span className="text-xs font-bold text-gray-800 w-8 text-right">{flow.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Model Usage */}
          {data.model_usage.length > 0 && (
            <div className="p-4 rounded-xl bg-white border border-violet-200 shadow-sm" data-testid="ai-report-models">
              <p className="text-xs font-medium text-violet-700 uppercase tracking-wider mb-3">Modell-Nutzung</p>
              <div className="space-y-2">
                {data.model_usage.map((model, i) => (
                  <div key={model.model} className="flex items-center justify-between" data-testid={`ai-report-model-${i}`}>
                    <span className="text-xs text-gray-700 truncate">{model.model}</span>
                    <span className="text-xs font-bold text-violet-700 ml-2">{model.count}x</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Recommendations */}
        {data.recommendations.length > 0 && (
          <div className="p-4 rounded-xl bg-white border border-violet-200 shadow-sm" data-testid="ai-report-recommendations">
            <p className="text-xs font-medium text-violet-700 uppercase tracking-wider mb-3">
              <BarChart3 className="w-3.5 h-3.5 inline mr-1" />
              Empfehlungen
            </p>
            <div className="space-y-2">
              {data.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  {rec.includes('grünen Bereich') || rec.includes('Weiter so') ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  )}
                  <span>{rec}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Errors */}
        {data.error_summary.length > 0 && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 shadow-sm" data-testid="ai-report-errors">
            <p className="text-xs font-medium text-red-700 uppercase tracking-wider mb-3">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              Top Fehler
            </p>
            <div className="space-y-1.5">
              {data.error_summary.map((err, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-red-700 truncate flex-1">{err.error}</span>
                  <span className="text-red-600 font-bold ml-2">{err.count}x</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIWeeklyReportWidget;
